import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PepPicker from "../components/PepPicker";
import { GAMES } from "../components/WagerPicker";
import { ArrowLeft, Maximize2, Coins, Loader2, Trophy, Gamepad2 } from "lucide-react";
import DraftBoard from "../components/DraftBoard";
import LiveSimulation from "../components/LiveSimulation";
import PitchView from "../components/PitchView";
import PrizesModal from "../components/PrizesModal";
import WalletModal from "../components/WalletModal";
import AuthForm from "../components/AuthForm";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { autoDraftXi, pepSeasonVs, PEP } from "../data/gameData";
import { auth, api, summaryFrom, getToken, getStoredUser } from "../lib/api";
import { GuestLock } from "../components/GuestUnlock";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import WinCelebration from "../components/WinCelebration";

const TopBar = ({ navigate, coins, credits = 0, onPrizes, onWallet }) => (
  <div className="sticky top-0 z-40 bg-[#0F172A]/85 backdrop-blur-md border-b border-white/5">
    <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
      <button
        data-testid="pep-back-btn"
        onClick={() => navigate("/")}
        className="pill-nav rounded-full px-3.5 py-1.5 flex items-center gap-2 text-sm font-medium text-slate-200"
      >
        <ArrowLeft className="w-4 h-4" /> Home
      </button>
      <div className="flex items-center gap-2">
        <img src={PEP.avatar} alt={PEP.name} className="w-7 h-7 rounded-full object-cover border border-[#10B981]/40" />
        <div className="font-display font-extrabold text-sm tracking-tight">
          <span className="text-white">vs</span> <span className="text-[#10B981]">{PEP.name}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {credits > 0 && (
          <div
            data-testid="pep-credits"
            title="Free Pep games"
            className="flex items-center gap-1.5 rounded-full bg-[#38BDF8]/15 border border-[#38BDF8]/40 px-3 py-1.5"
          >
            <Gamepad2 className="w-4 h-4 text-[#38BDF8]" />
            <span className="font-display font-bold text-sm text-[#38BDF8]">{credits}</span>
          </div>
        )}
        {onPrizes && (
          <button
            data-testid="pep-prizes-btn"
            onClick={onPrizes}
            title="Your prizes"
            className="rounded-full bg-white/5 border border-white/10 p-2 hover:border-[#10B981]/50 transition-colors"
          >
            <Trophy className="w-4 h-4 text-slate-200" />
          </button>
        )}
        <button
          data-testid="pep-coin-balance"
          onClick={onWallet}
          title="Open wallet"
          className="flex items-center gap-1.5 rounded-full bg-[#10B981]/12 border border-[#10B981]/30 px-3 py-1.5 hover:border-[#10B981]/60 transition-colors"
        >
          <Coins className="w-4 h-4 text-[#10B981]" />
          <span className="font-display font-bold text-sm text-[#10B981]" data-testid="pep-coins">{coins == null ? "—" : `£${coins.toLocaleString()}`}</span>
        <GuestLock />
        </button>
      </div>
    </div>
  </div>
);

const toCompact = (slot, revealed) => ({
  id: slot.id,
  x: slot.x,
  y: slot.y,
  label: slot.label,
  cat: slot.cat,
  player: revealed
    ? { short: slot.player.short, color: slot.player.color, rating: slot.player.rating, name: slot.player.name }
    : null,
});

const PepPreview = ({ board, revealed, onExpand }) => (
  <button
    data-testid="pep-preview-btn"
    onClick={onExpand}
    className="fixed bottom-4 right-4 z-40 w-28 sm:w-32 rounded-xl border border-white/15 bg-[#1E293B]/95 backdrop-blur p-2 shadow-xl hover:border-[#10B981]/60 transition-colors text-left"
  >
    <div className="flex items-center gap-1.5 mb-1">
      <img src={PEP.avatar} alt={PEP.name} className="w-5 h-5 rounded-full object-cover shrink-0" />
      <span className="text-[10px] font-semibold truncate text-white flex-1">Pep</span>
      {revealed >= 11 ? (
        <span className="text-[8px] font-bold text-emerald-400">READY</span>
      ) : (
        <span className="text-[8px] text-muted-38">{revealed}/11</span>
      )}
    </div>
    <div className="pointer-events-none">
      <PitchView board={board} compact />
    </div>
    <div className="flex items-center justify-center gap-1 text-[9px] text-muted-38 mt-1">
      <Maximize2 className="w-2.5 h-2.5" /> Pep is drafting
    </div>
  </button>
);

const PepMatch = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [user, setUser] = useState(getStoredUser());
  const [coins, setCoins] = useState(getStoredUser()?.coins ?? null);
  const [credits, setCredits] = useState(getStoredUser()?.pep_credits ?? 0);
  const [ready, setReady] = useState(false);
  const [pepXi, setPepXi] = useState(() => autoDraftXi());
  const [revealed, setRevealed] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [result, setResult] = useState(null);
  const [charging, setCharging] = useState(false);
  const [simDone, setSimDone] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [winCoins, setWinCoins] = useState(0);
  const [myBoard, setMyBoard] = useState(null);
  const [showPrizes, setShowPrizes] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [params] = useSearchParams();
  const stake = user ? Math.max(0, parseInt(params.get("stake"), 10) || 0) : 0;
  const choosing = !!user && !params.has("stake");

  useEffect(() => {
    auth.ensure()
      .then((u) => { setUser(u); if (u) { setCoins(u.coins); setCredits(u.pep_credits || 0); } setReady(true); })
      .catch(() => setReady(true));
  }, []);

  // Pep drafts his XI live while you draft yours.
  useEffect(() => {
    if (result || !user) return;
    const t = setInterval(() => {
      setRevealed((n) => (n >= 11 ? (clearInterval(t), 11) : n + 1));
    }, 2000);
    return () => clearInterval(t);
  }, [result, user]);

  const myName = user?.username || "You";
  const pepBoard = { formation: "433", filled: revealed, xi: pepXi.map((s, i) => toCompact(s, i < revealed)) };
  const pepFullBoard = { formation: "433", filled: 11, xi: pepXi.map((s) => toCompact(s, true)) };

  const handleSimulate = async (myResult) => {
    setCharging(true);
    try {
      const res = await api.pepPlay(stake);
      setCoins(res.coins);
      setCredits(res.pep_credits ?? credits);
      setUser((u) => (u ? { ...u, coins: res.coins, pep_credits: res.pep_credits ?? u.pep_credits } : u));
    } catch (e) {
      toast({ title: "Couldn't start the game", description: e?.response?.data?.detail || "Not enough balance" });
      setCharging(false);
      return;
    }
    const me = { email: "me", username: myName, summary: summaryFrom(myResult), board: myBoard };
    const pep = { email: "pep", username: PEP.name, avatar: PEP.avatar, summary: summaryFrom(pepSeasonVs(myResult.points, pepXi)), board: pepFullBoard };
    const winner =
      me.summary.points > pep.summary.points ? "me" : pep.summary.points > me.summary.points ? "pep" : "tie";
    setResult({ creator: me, opponent: pep, winner, status: "complete", stake: 0, pot: 0 });
    setCharging(false);
    setSimDone(false);
  };

  const playAgain = () => {
    setResult(null);
    setPepXi(autoDraftXi());
    setRevealed(0);
    setSimDone(false);
  };

  const onCoins = (c) => {
    setCoins(c);
    setUser((u) => (u ? { ...u, coins: c } : u));
  };

  if (!ready) {
    return (
      <div className="bg-app min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (result) {
    return (
      <div className="bg-app min-h-screen text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <TopBar navigate={navigate} coins={coins} onPrizes={user ? () => setShowPrizes(true) : undefined} onWallet={user ? () => setShowWallet(true) : undefined} />
        <LiveSimulation
          match={result}
          user={{ email: "me", username: myName }}
          onPlayAgain={playAgain}
          friendly
          onComplete={async () => {
            setSimDone(true);
            try {
              const res = await api.pepResult(
                result.winner === "me",
                result.creator?.summary?.points || 0,
                result.opponent?.summary?.points || 0,
                result.winner === "tie",
              );
              onCoins(res.coins);
              if (res.won) {
                setWinCoins(res.reward);
                setCelebrate(true);
              }
            } catch (e) { /* no-op */ }
          }}
        />
        <WinCelebration open={celebrate} amount={winCoins} label={`Draft XI · you beat ${PEP.name}`} onClose={() => setCelebrate(false)} onAgain={playAgain} againLabel={`Play Pep again${stake ? ` · £${stake}` : ""}`} />
        {user && <PrizesModal open={showPrizes} onClose={() => setShowPrizes(false)} />}
        {user && <WalletModal open={showWallet} onClose={() => setShowWallet(false)} coins={coins} />}
        <Toaster />
      </div>
    );
  }

  return (
    <div className="bg-app min-h-screen text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <TopBar navigate={navigate} coins={coins} credits={credits} onPrizes={user ? () => setShowPrizes(true) : undefined} onWallet={user ? () => setShowWallet(true) : undefined} />

      {!user ? (
        <div className="relative z-10 max-w-md mx-auto px-5 py-10">
          <AuthForm onAuthed={(u) => { setUser(u); setCoins(u.coins); setCredits(u.pep_credits || 0); }} title={`Sign up to play ${PEP.name}`} subtitle="Free to play. Create your account and get £30 virtual to start." />
        </div>
      ) : (
        <>
          <div className="max-w-5xl mx-auto px-4 pt-4">
            <div className="card-38 p-4 flex items-center gap-3 hover:translate-y-0">
              <img src={PEP.avatar} alt={PEP.name} className="w-12 h-12 rounded-full object-cover border border-[#10B981]/40 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-white text-sm">Draft your XI to face {PEP.name}</div>
                <div className="text-muted-38 text-xs">
                  Watch Pep build his side bottom-right while you draft.{stake ? ` £${stake} staked — beat him to win £${stake * 2}.` : ""}
                </div>
              </div>
            </div>
          </div>

          {charging ? (
            <div className="flex justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <DraftBoard onSimulate={handleSimulate} onBoardChange={setMyBoard} ctaLabel={`Play ${PEP.name}`} />
          )}

          <PepPreview board={pepBoard} revealed={revealed} onExpand={() => setExpanded(true)} />

          <Dialog open={expanded} onOpenChange={setExpanded}>
            <DialogContent aria-describedby={undefined} className="w-[calc(100%-2rem)] bg-[#1E293B] border border-white/10 text-white max-w-sm p-4">
              <div className="flex items-center gap-2 mb-1">
                <img src={PEP.avatar} alt={PEP.name} className="w-7 h-7 rounded-full object-cover" />
                <DialogTitle className="font-display font-bold text-lg">{PEP.name}'s XI</DialogTitle>
              </div>
              <div className="text-muted-38 text-xs mb-3">{revealed >= 11 ? "Team ready" : `${revealed}/11 drafted`}</div>
              <PitchView board={revealed >= 11 ? pepFullBoard : pepBoard} />
            </DialogContent>
          </Dialog>
        </>
      )}
      {user && <PrizesModal open={showPrizes} onClose={() => setShowPrizes(false)} />}
      {user && <WalletModal open={showWallet} onClose={() => setShowWallet(false)} coins={coins} />}
      {choosing && <PepPicker open game={GAMES[0]} user={{ ...user, coins: coins ?? user.coins }} onClose={() => navigate("/")} />}
      <Toaster />
    </div>
  );
};

export default PepMatch;
